import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminService } from "@/services/adminService";
import { sellerService } from "@/services/sellerService";
import { walletService } from "@/services/walletService";
import toast from "react-hot-toast";
import { toastApiError } from "@/lib/errorMessages";
import { useAuthStore } from "@/store/authStore";
import { Permission } from "@/types";
import {
  AdminUserDetailPage,
  AdminUserActivitiesSection,
  sellerDetailToAdmin,
  useAdminUserDetailLabels,
  useAdminUserContactLabels,
} from "@/components/admin/users";
import { resolveCatalogTranslation } from "@/lib/translationResolution";

export default function SellerDetailPage() {
  const { t, i18n } = useTranslation("sellers");
  const { sellerId } = useParams<{ sellerId: string }>();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();

  const detailLabels = useAdminUserDetailLabels({
    roleLabel: t("detail.roleLabel"),
    statRelevant: t("detail.statActivities"),
    loadDetail: t("detail.loadSeller"),
    loadError: t("detail.loadError"),
  });

  const contactLabels = useAdminUserContactLabels("sellers", {
    toastPasswordResetSent: t("detail.toastPasswordResetSent"),
  });

  const {
    data: seller,
    isLoading: isLoadingSeller,
    error: sellerError,
  } = useQuery({
    queryKey: ["admin", "seller", sellerId],
    queryFn: () => adminService.getSeller(sellerId!),
    enabled: !!sellerId,
  });

  const {
    data: activities,
    isLoading: isLoadingActivities,
    error: activitiesError,
  } = useQuery({
    queryKey: ["admin", "seller", sellerId, "activities"],
    queryFn: () => adminService.getSellerActivities(sellerId!),
    enabled: !!sellerId,
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (isActive: boolean) =>
      sellerService.updateSellerProfile(sellerId!, { is_active: isActive }),
    onSuccess: () => {
      toast.success(t("detail.toastStatusUpdated"));
      void queryClient.invalidateQueries({
        queryKey: ["admin", "seller", sellerId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["admin", "seller", sellerId, "activities"],
      });
      void queryClient.invalidateQueries({ queryKey: ["admin", "sellers"] });
    },
    onError: (err) => {
      toastApiError(err, "generic", t("detail.toastStatusError"));
    },
  });

  const updateDisplayNameMutation = useMutation({
    mutationFn: (name: string) =>
      sellerService.updateSellerProfile(sellerId!, { name }),
    onSuccess: () => {
      toast.success(detailLabels.toastDisplayNameUpdated);
      void queryClient.invalidateQueries({
        queryKey: ["admin", "seller", sellerId],
      });
      void queryClient.invalidateQueries({ queryKey: ["admin", "sellers"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
    onError: (err) => {
      toastApiError(err, "generic", detailLabels.toastDisplayNameError);
    },
  });

  const user = useMemo(
    () => (seller ? sellerDetailToAdmin(seller) : undefined),
    [seller],
  );

  const activitiesForFilter = activities?.map((a) => ({
    id: a.id,
    name: resolveCatalogTranslation(a.name_translations, i18n.language),
  }));

  const relevantCount = activities?.length ?? 0;

  return (
    <AdminUserDetailPage
      user={user}
      isLoading={isLoadingSeller}
      hasError={!!sellerError}
      listPath="/admin/users"
      labels={detailLabels}
      contactLabels={contactLabels}
      bookings={
        sellerId
          ? {
              path: `/sellers/${sellerId}/bookings`,
              title: t("detail.bookingsCardTitle"),
              seeBookings: t("detail.seeBookings"),
            }
          : undefined
      }
      canToggleActive={hasPermission(Permission.USERS_ACCOUNT_STATUS_UPDATE)}
      isTogglingActive={toggleActiveMutation.isPending}
      onToggleActive={() => {
        if (user) {
          toggleActiveMutation.mutate(!user.isActive);
        }
      }}
      canEditDisplayName={hasPermission(Permission.USERS_UPDATE_ANY)}
      isUpdatingDisplayName={updateDisplayNameMutation.isPending}
      onUpdateDisplayName={async (name) => {
        await updateDisplayNameMutation.mutateAsync(name);
      }}
      relevantCountOverride={relevantCount}
      contactActions={
        hasPermission(Permission.SELLERS_UPDATE_ANY)
          ? {
              onSendPasswordReset: async () => {
                await adminService.sendSellerPasswordResetEmail(sellerId!);
                void queryClient.invalidateQueries({
                  queryKey: ["admin", "seller", sellerId],
                });
              },
            }
          : undefined
      }
      transactions={
        sellerId
          ? {
              fetchTransactions: (params) =>
                walletService.getTransactions(sellerId, params),
              fetchStats: (params) =>
                walletService.getStats(sellerId, params),
              activities: activitiesForFilter,
            }
          : undefined
      }
      activitiesContent={
        sellerId ? (
          <AdminUserActivitiesSection
            ownerUserId={sellerId}
            activities={activities}
            isLoading={isLoadingActivities}
            error={!!activitiesError}
            canCreate={hasPermission(Permission.ACTIVITIES_CREATE)}
            title={t("detail.activitiesTitle", {
              count: activities?.length ?? 0,
            })}
            loadMessage={t("detail.loadActivities")}
            errorMessage={t("detail.activitiesError")}
            emptyTitle={t("detail.noActivitiesTitle")}
            emptyDescription={t("detail.noActivitiesDesc")}
          />
        ) : undefined
      }
    />
  );
}
